import React, { useEffect, useRef, useState } from 'react';
import { X, AlertCircle } from 'lucide-react';

interface BentoScanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onScan: (code: string) => Promise<{ success: boolean; message?: string } | undefined>;
}

const keypadRows = [
  ['A', 'B', 'C', 'D', 'E', 'F'],
  ['G', 'H', 'I', 'J', 'K', 'L'],
  ['M', 'N', 'O', 'P', 'Q', 'R'],
  ['S', 'T', 'U', 'V', 'W', 'X'],
  ['Y', 'Z', '0', '1', '2', '3'],
  ['4', '5', '6', '7', '8', '9'],
  ['-']
];

const BentoScanModal: React.FC<BentoScanModalProps> = ({ isOpen, onClose, onScan }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setCode('');
    setError('');
    setLoading(false);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
  }, [isOpen]);

  const refocusInput = () => {
    if (!isOpen) return;
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const extractOrderId = (scanCode: string) => {
    const trimmed = scanCode.trim();
    if (!trimmed) return '';

    if (trimmed.includes('-')) {
      const tail = trimmed.split('-').pop() || '';
      return tail.replace(/\D/g, '');
    }

    if (/^\d+$/.test(trimmed)) return trimmed;

    return trimmed.replace(/\D/g, '');
  };

  const handleScannerInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value.toUpperCase());
    setError('');
  };

  const handleScannerKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleKeyPress = (key: string) => {
    if (loading) return;
    if (key) {
      setCode((prev) => `${prev}${key}`.toUpperCase());
      setError('');
      refocusInput();
    }
  };

  const handleClear = () => {
    if (loading) return;
    setCode('');
    setError('');
    refocusInput();
  };

  const handleSubmit = async () => {
    if (!code.trim()) {
      setError('コードを入力してください');
      refocusInput();
      return;
    }
    const orderId = extractOrderId(code);
    if (!orderId) {
      setError('無効なコードです');
      refocusInput();
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await onScan(orderId);
      if (result?.success) {
        onClose();
        return;
      }
      setError(result?.message || '読み取りに失敗しました');
    } catch (err: any) {
      setError(err?.message || '読み取りに失敗しました');
    } finally {
      setLoading(false);
      refocusInput();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-xl relative shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">🧾 弁当会計</h2>
          <span className="text-sm text-gray-500">予約コード入力</span>
        </div>

        <input
          ref={inputRef}
          type="text"
          value={code}
          onChange={handleScannerInput}
          onKeyDown={handleScannerKeyDown}
          onBlur={refocusInput}
          className="absolute opacity-0 pointer-events-none"
          aria-hidden="true"
        />

        <div className="border-2 border-emerald-400 rounded-xl p-4 mb-4 text-center font-mono text-2xl tracking-widest bg-emerald-50">
          {code || 'スキャン待機中'}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        <div className="grid grid-cols-6 gap-2 mb-4">
          {keypadRows.flat().map((key) => (
            <button
              key={key}
              onClick={() => handleKeyPress(key)}
              className="py-4 rounded-lg font-bold text-base bg-gray-100 hover:bg-gray-200 transition-all active:scale-95"
            >
              {key}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={handleClear}
            className="py-4 rounded-xl font-bold text-lg bg-red-500 text-white hover:bg-red-600 transition-all active:scale-95"
            disabled={loading}
          >
            クリア
          </button>
          <button
            onClick={handleSubmit}
            className="py-4 rounded-xl font-bold text-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-all active:scale-95"
            disabled={loading}
          >
            {loading ? '確認中...' : '確定'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BentoScanModal;
